import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataGrid, Panel, PanelGrid } from '@eo/shared';

import type { CapacityClientRow } from './api/types';
import { makeClientCapacityGridConfig } from './config/gridConfig';
import { CLIENT_GRID_PREFIX } from './constants';
import {
  clientFilterSource,
  personFilterSource,
  parseSelection,
  portfolioName,
  selectionName,
  selectionSearch,
} from './helpers';
import { HoursComposition } from './HoursComposition';
import { MemberCapacity } from './MemberCapacity';
import { OwnerRollupCards } from './OwnerRollupCards';
import { PortfolioSummary } from './PortfolioSummary';
import { SelectionDetail } from './SelectionDetail';
import { TeamLoad } from './TeamLoad';
import type { CapacityBoardProps } from './types';

export function CapacityBoard({
  data,
  isAdmin,
  meId,
  onPlan,
  onQueryOptions,
  onGenerateExcess,
}: CapacityBoardProps) {
  const [searchParams] = useSearchParams();
  const { report } = data;
  const name = portfolioName(report);
  const owner = report.users.find((u) => u.userId === report.scope.ownerUserId) ?? null;
  const selection = parseSelection(Object.fromEntries(searchParams.entries()));

  // The config is rebuilt only when the client list changes, so the link it
  // builds has to read the current URL through a ref.
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;
  const clientHref = useCallback(
    (clientId: string) => selectionSearch(paramsRef.current, { kind: 'client', id: clientId }),
    [],
  );
  const personHref = (userId: string) =>
    selectionSearch(searchParams, { kind: 'member', id: userId });
  const pickedClient = searchParams.get(`${CLIENT_GRID_PREFIX}client`);
  const clientsConfig = useMemo(
    () =>
      makeClientCapacityGridConfig({
        clientHref,
        clients: clientFilterSource(data, report, pickedClient, onQueryOptions),
        people: personFilterSource(data, report, null, onQueryOptions),
        onPlan,
        showOwner: report.scope.ownerUserId === null,
      }),
    [clientHref, data, report, pickedClient, onQueryOptions, onPlan],
  );

  return (
    <PanelGrid>
      <Panel
        span={2}
        title={name ? `${name}’s portfolio` : 'All portfolios'}
        subtitle="Engagement is every client’s effective budget this month, supplements included."
      >
        <PortfolioSummary report={report} />
      </Panel>
      <Panel
        title="Where the hours sit"
        subtitle="How the engagement splits between fixed and variable, and who it is assigned to."
      >
        <HoursComposition totals={report.totals} />
      </Panel>
      {owner ? (
        <Panel
          title={owner.userId === meId ? 'My capacity' : `${owner.name}’s capacity`}
          subtitle="Committed client hours across every portfolio, against the hours available for client work."
        >
          <MemberCapacity member={owner} progress={report.progress} />
        </Panel>
      ) : (
        isAdmin && (
          <Panel
            title="Portfolio by CFO"
            subtitle="Each CFO's book of business this month — budget, logged, and overage across every client they own."
          >
            <OwnerRollupCards rows={report.portfolios} />
          </Panel>
        )
      )}
      <Panel
        span={2}
        title="Team load"
        subtitle="Hours logged on this portfolio against hours assigned on it. Pick a person to see their detail."
      >
        <TeamLoad users={report.users} progress={report.progress} personHref={personHref} />
      </Panel>
      {/* Spans the canvas: a budget-versus-actual grid is unreadable in half a
          column. */}
      <Panel
        span={2}
        title="Clients"
        subtitle="Set each client’s fixed and variable hours from this month on, and hand some to the Data team: the CFO keeps the rest. Pick a client to see its detail."
      >
        <DataGrid<CapacityClientRow>
          config={clientsConfig}
          rows={data.clientRows}
          pagination={data.clientsPagination}
        />
      </Panel>
      {selection && (
        <Panel
          span={2}
          title={selectionName(report, selection)}
          subtitle={
            selection.kind === 'client'
              ? 'Where this client’s month is heading, who worked on it, its time log, and its excess hours bill.'
              : 'This person’s capacity, how their month splits, their clients, and their time log.'
          }
        >
          <SelectionDetail
            data={data}
            selection={selection}
            onQueryOptions={onQueryOptions}
            onGenerateExcess={onGenerateExcess}
          />
        </Panel>
      )}
    </PanelGrid>
  );
}
